import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import type React from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import AuthCard from 'src/components/Layout/AuthLayout/AuthCard';
import ButtonWithLoader from 'src/components/Shared/ButtonWithLoader';
import InputField from 'src/components/Shared/FormFields/InputField';
import ToasterComponent from 'src/components/Shared/ToasterComponent';
import { useAuth } from 'src/context/AuthContext';
import useStatus from 'src/hooks/useStatus';
import { AUTH_ROUTES, COORDINATOR_ROUTES, VENDOR_ROUTES } from 'src/shared/constants/routes';
import { checkVendorDomain } from 'src/shared/utils/hosts';
import { safeInternalRedirect } from 'src/shared/utils/redirects';
import * as yup from 'yup';
import SocialSignInButtons from './components/SocialSignInButtons';
import { AUTH_FORM_VALIDATION, LOGIN_PAGE } from './constants';

type SignInFormInputs = {
  email: string;
  password: string;
};

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email(AUTH_FORM_VALIDATION.EMAIL_INVALID)
    .required(AUTH_FORM_VALIDATION.EMAIL_REQUIRED),
  password: yup.string().required(AUTH_FORM_VALIDATION.PASSWORD_REQUIRED),
});

const Login: React.FC = () => {
  const auth = useAuth();
  const {
    loading,
    success,
    successMessage,
    errorMessage,
    showMessage,
    setLoading,
    setError,
    setSuccess,
    resetState,
  } = useStatus('login');
  const router = useRouter();
  const searchParams = useSearchParams();

  const methods = useForm<SignInFormInputs>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    resetState();
    // An authenticated account that the backend rejects (e.g. an account without
    // access to this portal) is bounced here by the global 401 handler. Surface a
    // message instead of leaving the user on a silently-reset login form.
    const unauthorizedAccess = sessionStorage.getItem('unauthorizedAccess');
    if (unauthorizedAccess === 'true') {
      sessionStorage.removeItem('unauthorizedAccess');
      setError(LOGIN_PAGE.UNAUTHORIZED_ACCESS_MESSAGE);
      return;
    }
    // Check if user was redirected due to session expiration
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      sessionStorage.removeItem('sessionExpired');
      setError(LOGIN_PAGE.SESSION_EXPIRED_MESSAGE);
    }
  }, [resetState, setError]);

  const { handleSubmit } = methods;

  const onSubmit = async (data: SignInFormInputs) => {
    setLoading();
    const result = await auth.signIn(data?.email, data?.password);
    const rawRedirectUrl = sessionStorage.getItem('redirectUrl') || searchParams.get('redirectUrl');
    const redirectUrl = safeInternalRedirect(rawRedirectUrl);
    if (result.success) {
      const isVendor = checkVendorDomain();
      setSuccess(LOGIN_PAGE.SUCCESS_MESSAGE);
      if (result.navigate === 'set-new-password') {
        router.push(AUTH_ROUTES.NEW_PASSWORD);
      } else if (result.navigate === 'mfa-verification') {
        router.push(AUTH_ROUTES.MFA_VERIFICATION);
      } else if (result.navigate === 'sms-mfa-verification') {
        router.push(AUTH_ROUTES.SMS_MFA_VERIFICATION);
      } else if (redirectUrl) {
        // Mark the post-login window: if the backend rejects this authenticated
        // account with a 401 while loading the destination, the global 401 handler
        // treats it as an unauthorized account rather than a session expiry.
        sessionStorage.setItem('postLoginVerification', 'true');
        sessionStorage.removeItem('redirectUrl');
        globalThis.location.href = redirectUrl;
      } else {
        sessionStorage.setItem('postLoginVerification', 'true');
        // Evict a REJECTED candidate here and NOWHERE EARLIER. sessionStorage wins the `||` above,
        // so a value this guard refuses would otherwise shadow every later legitimate proxy-minted
        // `?redirectUrl=` for the rest of the tab session. Do NOT hoist above the branch chain: the
        // three `result.navigate` branches deliberately leave the value in place so the MFA /
        // set-new-password round-trip (MFAVerification.tsx:43 -> app/page.tsx:15 -> /login) can
        // resume it at AuthLayout. Scope note: the two failure branches below deliberately do not
        // evict either, so a rejected value still survives a FAILED login attempt — AuthLayout
        // evicts it on the next authenticated mount. [APX-387]
        sessionStorage.removeItem('redirectUrl');
        router.push(isVendor ? VENDOR_ROUTES.HOME : COORDINATOR_ROUTES.HOME);
      }
    } else if (result?.code === 'UserNotConfirmedException') {
      sessionStorage.setItem(
        'emailVerificationState',
        JSON.stringify({ userId: result?.username }),
      );
      router.push(AUTH_ROUTES.EMAIL_VERIFICATION);
    } else {
      if (result?.code === 'NetworkError') {
        setError(LOGIN_PAGE.ERROR_SOMETHING_WENT_WRONG);
      } else {
        setError(LOGIN_PAGE.ERROR_MESSAGE);
      }
      toast(
        <ToasterComponent
          type="error"
          icon="error"
          description={result?.message || 'Something Went Wrong'}
        />,
      );
    }
  };

  return (
    <AuthCard
      headerText={LOGIN_PAGE.TITLE}
      showAlert={showMessage}
      alertType={success ? 'success' : 'error'}
      alertMessage={success ? successMessage : errorMessage}
    >
      <FormProvider {...methods}>
        {!success && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="ls-auth-form"
            data-testid="auth.login-form"
          >
            <SocialSignInButtons providers={auth.authConfigDetails.providers} />
            <div className="ls-auth-bottom-section" data-testid="/signin">
              <InputField
                type="text"
                placeholder=""
                name="email"
                label="Email Address"
                data-testid="email-input"
                className="ls-form-control"
                size="has-md"
              />
              <InputField
                type="password"
                placeholder=""
                name="password"
                label="Password"
                formGroupClass="ls-auth-password"
                data-testid="password-input"
                className="ls-form-control"
                size="has-md"
              />
              <div className="ls-forgot-password">
                <Link
                  href={AUTH_ROUTES.FORGOT_PASSWORD}
                  className="ls-button ls-button-tertiary has-info"
                  data-testid="auth.forgot-password-link"
                >
                  {LOGIN_PAGE.LINK_LABEL}
                </Link>
              </div>

              <ButtonWithLoader
                disabled={!auth.isAuthInitialized}
                type="submit"
                className="ls-button ls-button-primary has-primary btn-block"
                isLoading={loading}
                label={LOGIN_PAGE.SUBMIT_LABEL}
                data-testid="auth.login-submit"
              />
              {auth?.domainNotValid ? (
                <p className="not-valid-domain">The organization not found.</p>
              ) : (
                ''
              )}
            </div>
          </form>
        )}
      </FormProvider>
    </AuthCard>
  );
};

export default Login;
